import { useState } from 'react';
import {
  Heading,
  BodyText,
  Card,
  Input,
  FormLabel,
  Select,
  Button,
  Switch,
} from '@aaimplatform/ui';

const STORAGE_KEY = 'aaim-padmin-preferences-v1';

interface Preferences {
  displayName: string;
  emailAlerts: boolean;
  tenantAlerts: boolean;
  systemAlerts: boolean;
  density: string;
  timezone: string;
  dateFormat: string;
}

const DEFAULT_PREFERENCES: Preferences = {
  displayName: 'Platform Admin',
  emailAlerts: true,
  tenantAlerts: true,
  systemAlerts: true,
  density: 'comfortable',
  timezone: 'America/New_York',
  dateFormat: 'MM/DD/YYYY',
};

function loadPreferences(): Preferences {
  if (typeof window === 'undefined') return DEFAULT_PREFERENCES;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFERENCES;
    const parsed = JSON.parse(raw) as Partial<Preferences>;
    return { ...DEFAULT_PREFERENCES, ...parsed };
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

function savePreferences(prefs: Preferences): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
}

export function PreferencesPage() {
  const [initial] = useState<Preferences>(() => loadPreferences());
  const [displayName, setDisplayName] = useState(initial.displayName);
  const [email] = useState('');
  const [emailAlerts, setEmailAlerts] = useState(initial.emailAlerts);
  const [tenantAlerts, setTenantAlerts] = useState(initial.tenantAlerts);
  const [systemAlerts, setSystemAlerts] = useState(initial.systemAlerts);
  const [density, setDensity] = useState(initial.density);
  const [timezone, setTimezone] = useState(initial.timezone);
  const [dateFormat, setDateFormat] = useState(initial.dateFormat);
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    savePreferences({
      displayName,
      emailAlerts,
      tenantAlerts,
      systemAlerts,
      density,
      timezone,
      dateFormat,
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div>

      {saved && (
        <div className="mb-4 p-4 bg-growth/10 border border-growth/20 text-growth rounded-sm">
          <BodyText variant="ui">Preferences saved.</BodyText>
        </div>
      )}

      <div className="space-y-6">
        <Card>
          <div className="p-6">
            <Heading level={3} as={3} size="card" className="mb-4">Profile</Heading>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <FormLabel>Display Name</FormLabel>
                <Input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="w-full"
                />
              </div>
              <div>
                <FormLabel>Email</FormLabel>
                <Input
                  value={email}
                  disabled
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-6">
            <Heading level={3} as={3} size="card" className="mb-4">Notification Preferences</Heading>
            <div className="space-y-4">
              <div className="flex items-center justify-between py-3 border-b border-surface">
                <div className="flex-1">
                  <BodyText variant="ui" className="font-medium">Email Alerts</BodyText>
                  <BodyText variant="ui" className="text-sm text-secondary">Receive email notifications for platform events</BodyText>
                </div>
                <Switch isSelected={emailAlerts} onChange={setEmailAlerts} size="sm" />
              </div>
              <div className="flex items-center justify-between py-3 border-b border-surface">
                <div className="flex-1">
                  <BodyText variant="ui" className="font-medium">Tenant Activity</BodyText>
                  <BodyText variant="ui" className="text-sm text-secondary">Notifications when tenants are created or change status</BodyText>
                </div>
                <Switch isSelected={tenantAlerts} onChange={setTenantAlerts} size="sm" />
              </div>
              <div className="flex items-center justify-between py-3">
                <div className="flex-1">
                  <BodyText variant="ui" className="font-medium">System Health Alerts</BodyText>
                  <BodyText variant="ui" className="text-sm text-secondary">Alerts for service outages and performance issues</BodyText>
                </div>
                <Switch isSelected={systemAlerts} onChange={setSystemAlerts} size="sm" />
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-6">
            <Heading level={3} as={3} size="card" className="mb-4">Display Settings</Heading>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <FormLabel>Density</FormLabel>
                <Select value={density} onChange={(e) => setDensity(e.target.value)} className="w-full">
                  <option value="comfortable">Comfortable</option>
                  <option value="compact">Compact</option>
                </Select>
              </div>
              <div>
                <FormLabel>Timezone</FormLabel>
                <Select value={timezone} onChange={(e) => setTimezone(e.target.value)} className="w-full">
                  <option value="America/New_York">Eastern Time (ET)</option>
                  <option value="America/Chicago">Central Time (CT)</option>
                  <option value="America/Denver">Mountain Time (MT)</option>
                  <option value="America/Los_Angeles">Pacific Time (PT)</option>
                  <option value="UTC">UTC</option>
                </Select>
              </div>
              <div>
                <FormLabel>Date Format</FormLabel>
                <Select value={dateFormat} onChange={(e) => setDateFormat(e.target.value)} className="w-full">
                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                  <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                </Select>
              </div>
            </div>
          </div>
        </Card>

        <div className="flex flex-col items-end gap-2">
          <Button onClick={handleSave}>Save Preferences</Button>
          <BodyText variant="ui" className="text-sm text-secondary">
            Preferences are stored in this browser. They do not sync across devices.
          </BodyText>
        </div>
      </div>
    </div>
  );
}
